// React
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Utils
import { COMPAIGNS_ACTION_TYPE, HAS_ENVELOPE } from '~/utils/constants';
import { MESSAGES } from '~/utils/message';

// MUI Components
import { Box, Tooltip, Typography, Paper, Skeleton } from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';
import { DownloadProofCIcon, GreenTickCIcon, PreviewProofCIcon } from '~/assets/images';

// Components
import ThumbnailModal from '~/components/Templates/GridView/ThumbnailModal';

// Styles
import './styles.scss';

const TABLE_HEADER_HEIGHT = 56;
const TABLE_ROW_HEIGHT = 70;
const GRID_BORDER_ALLOWANCE = 2;
const SCROLL_ACTIVATION_TOLERANCE = 8;
const CAMPAIGN_PRODUCTS_BOTTOM_GUTTER = 48;

const OrderedProducts = (props) => {
  const {
    selectedCampaign,
    downloadViewProof,
    downloadEnvelopeProof,
    downloadingRows,
    activeItemId,
  } = props;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedRowForPreview, setSelectedRowForPreview] = useState(null);
  const tableFrameRef = useRef(null);
  const [scrollFrameHeight, setScrollFrameHeight] = useState(null);

  useEffect(() => {
    if (selectedCampaign?.templates) {
      rows.length < 0 && setLoading(true);
      const computedRows = selectedCampaign?.templates.map(
        (
          {
            label,
            frontThumbnailUrl,
            backThumbnailUrl,
            productType,
            productId,
            isDownloaded,
            isEnvelopeDownloaded,
          },
          index
        ) => ({
          id: index,
          label: label,
          productType: productType,
          frontThumbnailUrl: frontThumbnailUrl,
          backThumbnailUrl: backThumbnailUrl,
          productId,
          isDownloaded,
          isEnvelopeDownloaded,
        })
      );
      setRows(computedRows);
      const loadingTimer = window.setTimeout(() => setLoading(false), 300);

      return () => window.clearTimeout(loadingTimer);
    } else {
      setRows([]);
      setLoading(false);
    }
  }, [JSON.stringify(selectedCampaign?.templates)]);

  useLayoutEffect(() => {
    if (selectedCampaign?.templates) {
      setLoading(true);
    }
  }, [selectedCampaign?.templates]);

  const LoadingSkeleton = () => (
    <Box className="skeletonContainer">
      <Box className="skeletonHeader">
        <Skeleton variant="text" width="30%" height={24} />
        <Skeleton variant="text" width="30%" height={24} />
        <Skeleton variant="rectangular" width={80} height={20} />
      </Box>
      {Array.from(
        {
          length: Math.max(selectedCampaign?.templates?.length || rows.length, 1),
        },
        (_, index) => index
      ).map((item) => (
        <Box key={item} className="skeletonRow">
          <Skeleton variant="text" width="30%" height={24} />
          <Skeleton variant="text" width="30%" height={24} />
          <Skeleton variant="rectangular" width={80} height={20} />
        </Box>
      ))}
    </Box>
  );

  const gridContentHeight = useMemo(() => {
    return TABLE_HEADER_HEIGHT + rows.length * TABLE_ROW_HEIGHT + GRID_BORDER_ALLOWANCE;
  }, [rows.length]);

  useLayoutEffect(() => {
    if (loading || rows.length === 0) {
      setScrollFrameHeight(null);
      return undefined;
    }

    let frameId;
    let resizeObserver;

    const measureTable = () => {
      const frame = tableFrameRef.current;
      const panel = frame?.closest('.campaignProductsPanel');

      if (!frame || !panel) {
        setScrollFrameHeight(null);
        return;
      }

      const availableHeight = panel.clientHeight - CAMPAIGN_PRODUCTS_BOTTOM_GUTTER;
      const nextScrollHeight =
        availableHeight > 0 && gridContentHeight > availableHeight + SCROLL_ACTIVATION_TOLERANCE
          ? availableHeight
          : null;

      setScrollFrameHeight((currentHeight) =>
        currentHeight === nextScrollHeight ? currentHeight : nextScrollHeight
      );
    };

    const scheduleMeasure = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = undefined;
        measureTable();
      });
    };

    measureTable();
    scheduleMeasure();

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleMeasure);

      if (tableFrameRef.current) {
        resizeObserver.observe(tableFrameRef.current);
        const panel = tableFrameRef.current.closest('.campaignProductsPanel');
        if (panel) resizeObserver.observe(panel);
      }
    } else {
      window.addEventListener('resize', scheduleMeasure);
    }

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [loading, rows.length, gridContentHeight]);

  const columns = [
    {
      field: 'templateName',
      headerName: 'Template',
      flex: 1.4,
      minWidth: 220,
      width: 280,
      sortable: false,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.label} placement="top-start">
            <Typography className="templateNameCell">{params.row.label || '-'}</Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'productType',
      headerName: 'Product',
      flex: 1,
      minWidth: 180,
      width: 220,
      sortable: false,
      renderCell: (params) => {
        return (
          <Tooltip
            title={
              COMPAIGNS_ACTION_TYPE.find(
                (product) => +product?.productId === +params?.row?.productId
              )?.label || params?.row?.productType
            }
            placement="top-start"
          >
            <Typography className="templateNameCell">
              {COMPAIGNS_ACTION_TYPE.find(
                (product) => +product?.productId === +params?.row?.productId
              )?.label || params?.row?.productType}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Preview Proof',
      headerName: ' ',
      align: 'center',
      headerAlign: 'center',
      minWidth: 64,
      maxWidth: 80,
      width: 64,
      sortable: false,
      renderCell: (params) => {
        return (
          // <Tooltip title="Preview Template">
          <Box
            component="img"
            src={PreviewProofCIcon}
            alt="Preview"
            style={{
              width: '20px',
              height: '20px',
              cursor: 'pointer',
            }}
            onClick={() => {
              setSelectedRowForPreview(params.row);
              setPreviewModalOpen(true);
            }}
          />
          // </Tooltip>
        );
      },
    },
  ];

  const templatesForModal = {
    rows: rows.map((row) => ({
      id: row.id,
      thumbnailUrl: row.frontThumbnailUrl,
      backThumbnailUrl:
        row.frontThumbnailUrl !== row.backThumbnailUrl ? row.backThumbnailUrl : null,
      title: row.label,
    })),
  };
  return (
    <Box className="thumnailTableContainer">
      {+activeItemId === 4 && (
        <Typography className="scheduledNote">
          {MESSAGES.ORDERS.CREATE.REVIEW.DESCRIPTION}
        </Typography>
      )}
      <Paper className="tableContainer" ref={tableFrameRef}>
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <Box className="dataGridWrapper">
            <DataGrid
              rows={rows}
              columns={columns}
              autoHeight={!scrollFrameHeight}
              compactColumns={false}
              measureContentWidth={false}
              preserveColumnLayout
              getRowId={(row) => row?.id}
              hideFooter
              hideFooterPagination
              hideFooterSelectedRowCount
              disableColumnMenu
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              disableRowSelectionOnClick
              className={`newMailDateTable ${
                scrollFrameHeight ? 'newMailDateTable--scrollFrame' : ''
              }`.trim()}
              columnHeaderHeight={TABLE_HEADER_HEIGHT}
              rowHeight={TABLE_ROW_HEIGHT}
              getRowHeight={() => TABLE_ROW_HEIGHT}
              sx={{
                ...(scrollFrameHeight ? { height: `${scrollFrameHeight}px` } : {}),
                '& .MuiDataGrid-root': {
                  border: 'none',
                },
                '& .MuiDataGrid-main': {
                  borderRadius: 'inherit',
                  overflow: 'hidden',
                },
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Preview Modal */}
      {!loading && (
        <ThumbnailModal
          openThumbnailModal={{
            open: previewModalOpen,
            id: selectedRowForPreview?.id,
          }}
          onClose={() => {
            setPreviewModalOpen(false);
            setSelectedRowForPreview(null);
          }}
          templates={templatesForModal}
        />
      )}
    </Box>
  );
};

export default OrderedProducts;
